import { useState } from 'react';
import type { ExportFormat, ExportSettings } from '../types';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onExportLocal: (fileName: string, format: ExportFormat, quality: number) => void;
  onExportFias: (settings: ExportSettings, fileName: string) => void;
  defaultFileName: string;
  canvasWidth: number;
  canvasHeight: number;
}

export function ExportDialog({
  isOpen,
  onClose,
  onExportLocal,
  onExportFias,
  defaultFileName,
  canvasWidth,
  canvasHeight,
}: ExportDialogProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [format, setFormat] = useState<ExportFormat>('png');
  const [quality, setQuality] = useState(92);
  const [width, setWidth] = useState(canvasWidth);
  const [height, setHeight] = useState(canvasHeight);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [destination, setDestination] = useState<'local' | 'fias'>('local');

  if (!isOpen) return null;

  const aspectRatio = canvasWidth / canvasHeight;

  const handleWidthChange = (w: number) => {
    setWidth(w);
    if (maintainAspectRatio) {
      setHeight(Math.round(w / aspectRatio));
    }
  };

  const handleHeightChange = (h: number) => {
    setHeight(h);
    if (maintainAspectRatio) {
      setWidth(Math.round(h * aspectRatio));
    }
  };

  const handleExport = () => {
    if (destination === 'local') {
      onExportLocal(fileName, format, quality / 100);
    } else {
      onExportFias({ format, quality, width, height, maintainAspectRatio }, fileName);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold">Export Image</h2>

        {/* File name */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">File name</span>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          />
        </label>

        {/* Format */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">Format</span>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          >
            <option value="png">PNG (lossless)</option>
            <option value="jpeg">JPEG</option>
            <option value="webp">WebP</option>
          </select>
        </label>

        {/* Quality (for lossy formats) */}
        {format !== 'png' && (
          <label className="block space-y-1">
            <span className="text-sm font-medium">Quality: {quality}%</span>
            <input
              type="range"
              min={10}
              max={100}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full"
            />
          </label>
        )}

        {/* Dimensions */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Dimensions</span>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={width}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md"
              min={1}
            />
            <span className="text-muted-foreground">x</span>
            <input
              type="number"
              value={height}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md"
              min={1}
            />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={maintainAspectRatio}
              onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-muted-foreground">Maintain aspect ratio</span>
          </label>
        </div>

        {/* Destination */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Save to</span>
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md border ${
                destination === 'local'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('local')}
            >
              Local download
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm rounded-md border ${
                destination === 'fias'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('fias')}
            >
              Fias files
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={handleExport}
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
}
